import { DeleteIcon } from "@chakra-ui/icons";
import { Box, Divider, HStack, VStack, Text, BoxProps } from "@chakra-ui/react";
import { FormikErrors, getIn } from "formik";
import React, { useMemo } from "react";
import { RegistrationFormConfigurationDetailFormValues } from "../../forms/RegistrationFormConfigurationDetailForm";
import { TextField } from "../fields/TextField/TextField";
import { Button, IconButton } from "@chakra-ui/button";
import { PlusIcon } from "../../styles/icons";
import { JsonTextAreaField } from "../fields/TextArea/JsonTextAreaField";

interface IntegrationObjectFieldProps {
  headerLabel: string;
  fieldName: string;
  keyValuePairs: { key: string; value: unknown }[] | null | undefined;
  editableKeys?: string[] | null;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  errors: FormikErrors<RegistrationFormConfigurationDetailFormValues>;
}

export const IntegrationObjectField = ({
  headerLabel,
  fieldName,
  keyValuePairs,
  editableKeys,
  setFieldValue,
  errors,
  ...props
}: IntegrationObjectFieldProps & BoxProps) => {
  const dep = JSON.stringify(keyValuePairs);

  const view = useMemo(() => {
    return (
      <Box
        w="100%"
        p={4}
        border="1px solid"
        borderColor="cherryButtonDark.50"
        borderRadius="md"
        {...props}
      >
        <HStack w="100%">
          <Text flexGrow={2} fontSize="sm">
            <b>{headerLabel}</b> :
          </Text>
          <Button
            size="xs"
            leftIcon={<PlusIcon />}
            onClick={() => {
              if (keyValuePairs) {
                keyValuePairs?.push({ key: "", value: "" });
                setFieldValue(fieldName, keyValuePairs);
              } else {
                setFieldValue(fieldName, [{ key: "", value: "" }]);
              }
            }}
            colorScheme="cherryButton"
          >
            New {headerLabel}
          </Button>
        </HStack>

        <Divider mb={2} mt={1} />
        <VStack w="100%">
          {(keyValuePairs?.length &&
            keyValuePairs?.map((kvp, kvpIdx) => {
              const editable = !editableKeys
                ? true
                : editableKeys.findIndex((key) => key == kvp.key) == -1;
              return (
                <HStack w="100%" key={kvpIdx}>
                  <TextField
                    alignSelf="flex-start"
                    label={`Key ${kvpIdx + 1}`}
                    name={`${fieldName}[${kvpIdx}].key`}
                    isDisabled={!editable}
                  />
                  <JsonTextAreaField
                    name={`${fieldName}[${kvpIdx}].value`}
                    label={`Value ${kvpIdx + 1}`}
                    valueText={kvp.value}
                    setFieldValue={setFieldValue}
                    errors={getIn(errors, `${fieldName}[${kvpIdx}].value`)}
                    rows={1}
                    isDisabled={!editable}
                  />
                  <Box h="100%" pb={1} alignSelf="flex-end">
                    <IconButton
                      aria-label={`Delete ${headerLabel}.`}
                      title={`Delete ${headerLabel}.`}
                      icon={<DeleteIcon />}
                      size="sm"
                      onClick={() => {
                        keyValuePairs?.splice(kvpIdx, 1);
                        setFieldValue(fieldName, keyValuePairs);
                      }}
                      isDisabled={!editable}
                    />
                  </Box>
                </HStack>
              );
            })) || (
            <Text fontStyle="italic" fontSize="sm">
              {headerLabel} has not been set.
            </Text>
          )}
        </VStack>
      </Box>
    );
  }, [dep, errors]);

  return <>{view}</>;
};
